import { useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Wallet, Receipt, CheckSquare, BarChart3, Building2 } from 'lucide-react';
import { useAuth } from '../lib/auth';
import type { Role } from '../lib/api';

const ROLE_HOME: Record<Role, string> = {
  platform_admin: '/admin/businesses',
  ceo: '/reports',
  finance: '/finance',
  director: '/approvals',
  head_of_dept: '/approvals',
  employee: '/expenses/mine',
};

export default function Home() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600" /></div>;
  }

  if (user) return <Navigate to={ROLE_HOME[user.role]} replace />;

  return (
    <div className="max-w-4xl mx-auto text-center py-16 space-y-10">
      <div>
        <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <Wallet className="w-9 h-9 text-green-600" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900">Expense claims, done properly</h1>
        <p className="text-gray-500 mt-3 text-lg max-w-xl mx-auto">
          Submit, approve, and pay expense claims with a clear budget trail — built for charities and businesses with departments to manage.
        </p>
        <div className="mt-8">
          <Link to="/login" className="btn-primary px-8 py-3 inline-block">Sign In</Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 text-left">
        <div className="card space-y-2">
          <Receipt className="w-6 h-6 text-green-600" />
          <h3 className="font-semibold text-gray-900">Submit with receipts</h3>
          <p className="text-sm text-gray-500">Employees submit expenses against their department, with a receipt attached.</p>
        </div>
        <div className="card space-y-2">
          <CheckSquare className="w-6 h-6 text-green-600" />
          <h3 className="font-semibold text-gray-900">Clear approvals</h3>
          <p className="text-sm text-gray-500">Line-manager approval, then finance sign-off, before anything gets paid.</p>
        </div>
        <div className="card space-y-2">
          <BarChart3 className="w-6 h-6 text-green-600" />
          <h3 className="font-semibold text-gray-900">Budget tracking</h3>
          <p className="text-sm text-gray-500">Approved spend comes straight out of each department's annual budget.</p>
        </div>
        <div className="card space-y-2">
          <Building2 className="w-6 h-6 text-green-600" />
          <h3 className="font-semibold text-gray-900">Built for org structure</h3>
          <p className="text-sm text-gray-500">Directorates, departments, and roles configured to match how you're organized.</p>
        </div>
      </div>
    </div>
  );
}
